/**
 * Format a date according to the specified format
 * @param {Date} date - The date to format
 * @param {string} format - The format string (e.g., 'DD MMMM YYYY')
 * @returns {string} The formatted date string
 */
export function formatDate(date, format) {
  if (!date) return '';

  // Ensure date is a Date object
  const d = date instanceof Date ? date : new Date(date);

  // Check if date is valid
  if (isNaN(d.getTime())) return '';

  // Define month names
  const monthNames = [
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember',
  ];

  // Define day names
  const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

  // Get date components
  const day = d.getDate();
  const month = d.getMonth();
  const year = d.getFullYear();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();

  // Format the date according to the specified format
  // Use regex with word boundaries to ensure we only replace complete tokens
  let result = format;

  // Create a mapping of format tokens to their values
  const formatMap = {
    YYYY: year,
    MMMM: monthNames[month],
    MMM: monthNames[month].substring(0, 3),
    MM: (month + 1).toString().padStart(2, '0'),
    M: (month + 1).toString(),
    DD: day.toString().padStart(2, '0'),
    D: day,
    dddd: dayNames[d.getDay()],
    ddd: dayNames[d.getDay()].substring(0, 3),
    HH: hours.toString().padStart(2, '0'),
    H: hours,
    mm: minutes.toString().padStart(2, '0'),
    m: minutes,
    ss: seconds.toString().padStart(2, '0'),
    s: seconds,
  };

  // Sort format tokens by length (longest first) to avoid partial replacements
  const tokens = Object.keys(formatMap).sort((a, b) => b.length - a.length);

  // Replace each token with its value
  for (const token of tokens) {
    // Use a regular expression with word boundaries to match exact tokens
    const regex = new RegExp(`\\b${token}\\b`, 'g');
    result = result.replace(regex, formatMap[token]);
  }

  return result;
}

/**
 * Format a date relative to the current time (e.g., "2 hours ago")
 * @param {Date} date - The date to format
 * @returns {string} The relative time string
 */
export function formatRelativeTime(date) {
  if (!date) return '';

  // Ensure date is a Date object
  const d = date instanceof Date ? date : new Date(date);

  // Check if date is valid
  if (isNaN(d.getTime())) return '';

  const now = new Date();
  const diffMs = now - d;
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);

  if (diffSec < 60) {
    return 'Baru saja';
  } else if (diffMin < 60) {
    return `${diffMin} menit yang lalu`;
  } else if (diffHour < 24) {
    return `${diffHour} jam yang lalu`;
  } else if (diffDay < 30) {
    return `${diffDay} hari yang lalu`;
  } else {
    // If more than 30 days, return the formatted date
    return formatDate(d, 'DD MMMM YYYY');
  }
}
